import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation } from '@/hoslog-intl/Translation';
import { DeleteIcon, EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { commaSeparator } from '@/lib/commaSeparator';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import { HoslogPlans, PLAN_STATUS } from '@/services/hoslog-plans/hoslog-plans.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ModalVariant } from '../../location/table/Column';
import HoslogPlansForm from '../HoslogPlansForm';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

enum status {
  Active = 'Active',
}

export function getColumns(
  onOpen: OnOpenProps,
  mutate: UseMutateAsyncFunction<
    {
      message: string;
      data: {
        planId: string;
        status: string;
      };
    },
    StandardizedApiError,
    {
      planId: string;
      status: 'A' | 'B' | 'D';
    },
    unknown
  >,
  t: (key: string) => string,
  openConfirmation: (data: {
    title: string;
    description: string;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction: () => void | Promise<void>;
    onCancel: () => void;
  }) => void,
): ColumnDef<HoslogPlans>[] {
  return [
    {
      accessorKey: 'sno',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="truncate max-w-[10rem] text-caption font-medium">{getSerialNumber(table, row.index)}</div>
      ),
      enableHiding: false,
      enableSorting: false,
    },

    {
      accessorKey: 'planType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.type')} />,
      cell: ({ row }) => {
        const type: string = row.getValue('planType');
        const className = type.toLowerCase() === 'basic' ? 'text-success' : 'text-link';
        return (
          <div className={`truncate max-w-[20rem] ${className} text-caption font-medium`}>
            {type.toLowerCase() === 'basic' ? t('common.page.table.basic') : t('common.page.table.premium')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'planTitle',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planTitle')} />,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-primary text-caption font-medium">{row.getValue('planTitle')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'planTime',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.planTime')}
        />
      ),
      cell: ({ row }) => <div className="text-primary text-caption font-medium">{row.getValue('planTime')}分</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'noOfPeople',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.numberOfPeople')}
        />
      ),
      cell: ({ row }) => <div className="text-primary text-caption font-medium">{row.getValue('noOfPeople')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'price',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.planPrice')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-right text-primary text-caption font-medium">
          {commaSeparator(row.getValue('price'))} 円
        </div>
      ),
      meta: { align: 'right' },
      enableHiding: false,
    },
    {
      accessorKey: 'planStatus',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.status')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-primary text-caption font-medium">
          {row.getValue('planStatus') === status.Active ? (
            <p className="text-success">
              <Translation id="common.page.table.active" />
            </p>
          ) : (
            <p className="text-destructive-text">
              <Translation id="common.page.table.blocked" />
            </p>
          )}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.updatedDate')}
        />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue<Date>('updatedDate'));
        return (
          <div className="whitespace-nowrap text-primary text-caption font-medium">
            {formatDate(date, 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      id: 'actions',
      accessorKey: 'Actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.actions')}
        />
      ),
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          {row.getValue('planStatus') === status.Active && (
            <Can rule={{ allOf: [PERMS.plan.detailView, PERMS.plan.manage] }}>
              <EditIcon
                className="text-foreground-subtle size-4 hover:cursor-pointer"
                onClick={() =>
                  onOpen({
                    modalTitle: 'configurationManagement.plans.page.managePlansModal.hoslogPlans',
                    title: 'Hoslog Plans',
                    okText: t('common.page.modal.submit'),
                    cancelText: t('common.page.button.cancel'),
                    size: 'lg',
                    component: <HoslogPlansForm planId={row.original.planId} />,
                  })
                }
              />
            </Can>
          )}
          <Button
            variant={'ghost'}
            className="p-0"
            onClick={() => {
              const isActive = row.getValue('planStatus') === status.Active;
              openConfirmation({
                title: t('common.page.modal.confirm'),
                description: isActive
                  ? t('configurationManagement.plans.page.statusUpdateModel.block')
                  : t('configurationManagement.plans.page.statusUpdateModel.unblock'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                size: 'lg',
                onAction: async () => {
                  await mutate({
                    planId: row.original.planId,
                    status: row.getValue('planStatus') === status.Active ? 'B' : 'A',
                  });
                },
                onCancel: () => {},
                variant: 'warning',
              });
            }}
          >
            {row.getValue('planStatus') === status.Active ? (
              <LockCloseIcon className="text-destructive-text size-4 " />
            ) : (
              <LockOpenIcon className="text-link size-4 " />
            )}
          </Button>
          {row.getValue('planStatus') === status.Active && (
            <Can rule={{ allOf: [PERMS.plan.statusUpdate] }}>
              <Button
                variant={'ghost'}
                className="p-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    onAction: async () => {
                      await mutate({
                        planId: row.original.planId,
                        status: PLAN_STATUS.DELETE,
                      });
                    },
                    onCancel: () => {},
                    variant: 'destructive',
                  });
                }}
              >
                <DeleteIcon className="text-destructive-text size-4 " />
              </Button>
            </Can>
          )}
        </div>
      ),
      size: 40,
    },
  ];
}
